import { createApp } from 'vue'
import { any, nodeIsDebug } from './waitUtils'
import vWaitComponent from './v-wait.vue'
import vWaitDirective from './waitDirectives'
import { useWaitStore } from 'client/stores/wait'
import { mapActions, mapState } from 'pinia'

export default class VueWait {
  constructor(options = {}) {
    const defaults = {
      accessorName: '$wait',
      // Components
      registerComponent: true,
      componentName: 'v-wait',
      // Directives
      registerDirective: true,
      directiveName: 'wait'
    }
    this.options = {
      ...defaults,
      ...options
    }
    this.initialized = false
  }

  init(App) {
    if (this.initialized) {
      return
    }

    if (this.options.registerComponent) {
      App.component(this.options.componentName, vWaitComponent)
    }

    if (this.options.registerDirective) {
      App.directive(this.options.directiveName, vWaitDirective)
    }

    const config = {
      template: '<template></template>',
      computed: {
        ...mapState(useWaitStore, ['waitingFor', 'progresses']),
        any() {
          return any(this.waitingFor)
        }
      },
      methods: {
        ...mapActions(useWaitStore, ['start', 'end', 'progress', 'is', 'percent'])
      }
    }

    this.stateHandler = createApp(config)
      .mount(document.createElement('div'))
    this.initialized = true
  }

  get any() {
    return this.stateHandler.any
  }

  is(waiter) {
    return this.stateHandler.is(waiter)
  }

  // alias for `is`
  waiting(waiter) {
    return this.is(waiter)
  }

  percent(waiter) {
    return this.stateHandler.percent(waiter)
  }

  start(waiter) {
    return this.stateHandler.start(waiter)
  }

  end(waiter) {
    return this.stateHandler.end(waiter)
  }

  progress(waiter, current, total = 100) {
    if (!this.is(waiter)) {
      this.start(waiter)
    }

    if (current > total) {
      this.end(waiter)
      return
    }

    return this.stateHandler.progress({ waiter, current, total })
  }
}

export function install(App) {
  if (install.installed && App) {
    if (nodeIsDebug()) {
      console.warn(
        '[vue-wait] already installed. Vue.use(VueWait) should be called only once.'
      )
    }
    return
  }

  App.mixin({
    /**
     * VueWait init hook, injected into each instances init hooks list.
     */
    beforeCreate() {
      const { wait, parent } = this.$options

      let instance = null
      if (wait) {
        instance = typeof wait === 'function' ? new wait() : wait
        instance.init(App)
      } else if (parent && parent.__$waitInstance) {
        instance = parent.__$waitInstance
        instance.init(App)
      }

      if (instance) {
        // Store helper for internal use
        this.__$waitInstance = instance
        this[instance.options.accessorName] = instance
      }
    }
  })

  install.installed = true
}

function createVueWait(options) {
  const Wait = {
    instance: null,
    async install(app) {
      if (this.installed && app) {
        if (nodeIsDebug()) {
          console.warn('[vue-wait] already installed')
        }
        return this.instance
      }

      const instance = new VueWait(options)
      instance.init(app)
      app.config.globalProperties[instance.options.accessorName] = instance

      app.mixin({
        beforeCreate() {
          this.__$waitInstance = instance
        }
      })

      this.instance = instance
      this.installed = true
      return instance
    }
  }

  return Wait
}

// Export which are imported to export
export { createVueWait }

VueWait.install = install
